import cx from '@/lib/cx';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useSnapCarousel } from 'react-snap-carousel';

interface CarouselProps<T> {
  readonly items: T[];
  readonly renderItem: (props: CarouselRenderItemProps<T>) => React.ReactElement<CarouselItemProps>;
}

interface CarouselRenderItemProps<T> {
  readonly item: T;
  readonly isSnapPoint: boolean;
}

type PaginationDotsProps = { total: number; current: number; goTo: (index: number) => void };

const PaginationDots = ({ total, current, goTo }: PaginationDotsProps) => {
  return (
    <div className="flex justify-center items-center space-x-2">
      {[...Array(total)].map((_, index) => (
        <button
          key={index}
          onClick={() => goTo(index)}
          className={cx(
            'w-4 h-4 rounded-full border-2 border-gray-400 transition-all duration-300 bg-theme-primary',
            { 'opacity-30': index !== current }
          )}
        />
      ))}
    </div>
  );
};

export const Carousel = <T,>({ items, renderItem }: CarouselProps<T>) => {
  const {
    scrollRef,
    pages,
    activePageIndex,
    hasPrevPage,
    hasNextPage,
    prev,
    next,
    snapPointIndexes,
    goTo,
  } = useSnapCarousel();
  return (
    <div className="w-full h-full relative">
      <ul className="snap-x snap-mandatory flex overflow-auto relative hide-scroll" ref={scrollRef}>
        {items.map((item, i) =>
          renderItem({
            item,
            isSnapPoint: snapPointIndexes.has(i),
          })
        )}
      </ul>
      <button
        className={cx(
          'absolute top-[46%] left-0 translate-y-[-50%] z-10 bg-theme-primary border border-gray-400 border-2 rounded-full w-8 h-8 flex items-center justify-center',
          {
            'opacity-30': !hasPrevPage,
          }
        )}
        onClick={() => prev()}
        disabled={!hasPrevPage}
      >
        <FontAwesomeIcon icon={faChevronLeft} color="white" />
      </button>

      <button
        className={cx(
          'absolute top-[46%] right-0 translate-y-[-50%] z-10 bg-theme-primary border border-gray-400 border-2 rounded-full w-8 h-8 flex items-center justify-center',
          {
            'opacity-30': !hasNextPage,
          }
        )}
        onClick={() => next()}
        disabled={!hasNextPage}
      >
        <FontAwesomeIcon icon={faChevronRight} color="white" />
      </button>
      <div className="flex justify-center mt-4">
        <PaginationDots total={pages.length} current={activePageIndex} goTo={goTo} />
      </div>
    </div>
  );
};

interface CarouselItemProps {
  readonly isSnapPoint: boolean;
  readonly children?: React.ReactNode;
}

export const CarouselItem = ({ isSnapPoint, children }: CarouselItemProps) => (
  <li
    className={cx('overflow-hidden w-full h-full shrink-0', {
      'snap-start': isSnapPoint,
    })}
  >
    {children}
  </li>
);
